/* eslint-disable no-console */
import BTCookies from '../../../common/cookies';
import {BTTrackerBase} from '../base';
import {HasOffersNetwork} from './network/hasoffers';
import {TOPIC_BIOTRUST_OFFER_ID} from '../../messagehub/topics';

export const AFFILIATE_NETWORKS_COOKIE = 'affNetworkOffers';

export class AffiliateNetworksTracker extends BTTrackerBase {
  constructor(args) {
    super(args);
    this.affiliateNetworkTrackers = [];
  }

  getName() {
    return 'AffiliateNetworksTracker';
  }

  configure(config) {
    super.configure(config);
    const {hasoffers, hasOffersLoggingEnabled = false} = config;
    this.hasoffers = hasoffers;
    this.hasOffersLoggingEnabled = hasOffersLoggingEnabled;

    // clear network trackers from a previous configure call (to handle re-configuration situations)
    this.affiliateNetworkTrackers.splice(0);

    if (this.hasoffers) {
      this.affiliateNetworkTrackers.push(new HasOffersNetwork({networkUrl: hasoffers, hasOffersLoggingEnabled}));
    }
  }

  get networks() {
    const {hostname} = window.location;
    let domain = hostname;
    if (hostname?.endsWith('biotrust.com')) {
      domain = 'biotrust.com';
    }
    const val = BTCookies.getJSON(AFFILIATE_NETWORKS_COOKIE, {domain}) || {};
    this._logWithCatchJs(`[${this.getName()}] Got network cookie value`, {value: JSON.stringify(val)});
    return val;
  }

  set networks(val) {
    const {hostname} = window.location;
    let domain = hostname;
    // if the current hostname is a part of biotrust.com, ensure that it can be read regardless of the subdomain.
    // This does have the side effect that staging cookies will be visible in live, and vice versa ... but that's a
    // problem for future-us 🤷‍️
    if (hostname?.endsWith('biotrust.com')) {
      domain = 'biotrust.com';
    }
    this._logWithCatchJs(`[${this.getName()}] Setting network cookie`, {value: JSON.stringify(val), domain});
    BTCookies.set(AFFILIATE_NETWORKS_COOKIE, val, {expires: 1, domain});
  }

  init(...params) {
    super.init(...params);
    if (!this.cj && !this.hasoffers) {
      this._logWithCatchJs(`[${this.getName()}] Both CJ and has offers were not specified. Tracker disabled.`);
      this.disable();
      return;
    }

    const initTrackers = () => {
      if (typeof window.optimize_callback === 'function') {
        window.optimize_callback(() => {
          this.trackNetworkOffers();
          this.messageHub.subscribe({topic: TOPIC_BIOTRUST_OFFER_ID, handler: msg => this.trackNetworkOffers(msg)});
        });
      } else {
        this.trackNetworkOffers();
        this.messageHub.subscribe({topic: TOPIC_BIOTRUST_OFFER_ID, handler: msg => this.trackNetworkOffers(msg)});
      }
    };

    if (window.document.readyState === 'loading') {
      window.document.addEventListener('DOMContentLoaded', initTrackers);
    } else {
      initTrackers();
    }
  }

  trackNetworkOffers(params) {
    const updatedNetworks = this.affiliateNetworkTrackers.map(n => n.track(params))
      .filter(v => v !== null && typeof v === 'object')
      .reduce((net, {key, value}) => Object.assign(net, {[key]: value}), {});

    this.networks = Object.assign(this.networks, updatedNetworks);
  }

  purchase(order, items, options) {
    if (Object.values(this.networks).length === 0) {
      this._logWithCatchJs(
        `[${this.getName()}] No affiliate network tracking information found. Skipping Affiliate Purchase Tracking.`,
      );
      return;
    }
    this._logWithCatchJs(`[${this.getName()}] affNetworks`, this.networks);

    const networksConverted = {};
    const updatedOffers = Object.entries(this.networks)
      // eslint-disable-next-line no-unused-vars
      .sort(([key1, val1], [key2, val2]) => {
        if (val1.ts > val2.ts) {
          return -1;
        }
        if (val1.ts === val2.ts) {
          return 0;
        }
        return 1;
      })
      .reduce((result, [key, networkOffer]) => {
        if (networkOffer.completed) {
          this._logWithCatchJs(`[${this.getName()}] Skipping aff network because it's completed`, networkOffer);
        } else {
          const [network] = this.affiliateNetworkTrackers.filter(t => t.networkType === networkOffer.type);
          if (networksConverted[networkOffer.type] === true) {
          // don't allow the same network to convert more than once per purchase
            this._logWithCatchJs(
              `[${this.getName()}] Skipping conversion for network type ${networkOffer.type}`
              + '-- already converted for this purchase',
              networkOffer,
            );
          } else if (network) {
            networkOffer.completed = network.convert(
              order,
              items,
              options,
              networkOffer,
            );
            if (networkOffer.completed) {
              networksConverted[networkOffer.type] = true;
            }
          } else {
            this._logWithCatchJs(`[${this.getName()}] Unknown network type in affNetworkOffers`, networkOffer.type);
          }
        }

        return Object.assign(result, {[key]: networkOffer});
      }, {});

    this.networks = Object.assign(this.networks, updatedOffers);
  }

  _logWithCatchJs(message, ...rest) {
    console.log(message, ...rest);

    if (this.hasOffersLoggingEnabled) {
      let cookieErr = null;
      let sessionId = window.btDataLayer?.sessionId;
      if (!sessionId) {
        try {
          sessionId = BTCookies.get(window.bt?.settings?.sessionCookieName);
        } catch (ex) {
          sessionId = 'failed-to-read-session-cookie';
          cookieErr = ex;
        }
      }

      if (window && window.catchjs && window.catchjs.log) {
        window.catchjs.log(`${message} (cpSessionId: ${sessionId || 'not-set'})`, {
          ...rest,
          ts: Date.now(),
          cookieErr,
        });
      }
    }
  }
}
